import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import type { BatchCharacteristicDraft } from "@/types/batch-characteristics.types";
import { ValueListField } from "./ValueListField";
import { MarkingLinesField } from "./MarkingLinesField";

interface CharacteristicEditorProps {
  draft: BatchCharacteristicDraft;
  setDraft: (patch: Partial<BatchCharacteristicDraft>) => void;
  lines: Array<{ id: string; name: string }>;
  problem: string | null;
  isSaving: boolean;
  onSave: () => void;
  onCancel: () => void;
}

/** Spec 084 — declarar uma característica: nome, tipo, obrigatoriedade, valores e linhas. */
export function CharacteristicEditor(props: CharacteristicEditorProps) {
  const { draft, setDraft, lines, problem, isSaving, onSave, onCancel } = props;
  const editando = draft.id !== null;
  return (
    <div className="space-y-4 rounded-md border border-border-default p-4">
      <div className="grid gap-3 md:grid-cols-3">
        <div className="space-y-1.5">
          <Label htmlFor="characteristic-name">Nome</Label>
          <Input
            id="characteristic-name"
            placeholder="Ex.: Variedade"
            maxLength={60}
            value={draft.name}
            onChange={(e) => setDraft({ name: e.target.value })}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="characteristic-kind">Tipo</Label>
          <Select
            id="characteristic-kind"
            value={draft.kind}
            // O tipo é imutável: trocar lista por texto invalidaria os valores já gravados.
            disabled={editando}
            onChange={(e) => setDraft({ kind: e.target.value as BatchCharacteristicDraft["kind"] })}
          >
            <option value="LIST">Lista de valores</option>
            <option value="TEXT">Texto livre</option>
          </Select>
          {editando ? (
            <p className="text-[11px] text-text-tertiary">O tipo não muda depois de criado.</p>
          ) : null}
        </div>
        <div className="flex items-center gap-2 pt-6 text-[13px]">
          <input
            type="checkbox"
            id="characteristic-required"
            className="accent-rd-primary-500 h-3.5 w-3.5"
            checked={draft.required}
            onChange={(e) => setDraft({ required: e.target.checked })}
          />
          <label htmlFor="characteristic-required" className="text-text-primary">
            Obrigatória nas linhas de marcação
          </label>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {draft.kind === "LIST" ? (
          <ValueListField values={draft.values} onChange={(values) => setDraft({ values })} />
        ) : (
          <p className="text-[12px] text-text-secondary">
            Texto livre: quem marca a batelada digita o valor (um código de lote, por exemplo).
          </p>
        )}
        <MarkingLinesField
          lines={lines}
          selected={draft.lineIds}
          onChange={(lineIds) => setDraft({ lineIds })}
        />
      </div>

      <div className="flex flex-wrap items-center justify-end gap-2">
        {problem ? <span className="text-[12px] text-text-secondary">{problem}</span> : null}
        <Button variant="outline" onClick={onCancel}>
          Cancelar
        </Button>
        <Button onClick={onSave} disabled={problem !== null || isSaving}>
          Salvar característica
        </Button>
      </div>
    </div>
  );
}
